import React from "react";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { ORG_PALETTE, getOrgTokens } from "../../theme/style/organizationPalette";

const SegmentedTabs = ({
  options = [],
  selected,
  setSelected,
  activeColor = ORG_PALETTE.segmentedTabs.activeColor || "#659d33",
  wrap = false,
  dense = false,
  sx = {},
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const containerBg = isDark
    ? ORG_PALETTE.segmentedTabs.containerBgDk
    : ORG_PALETTE.segmentedTabs.containerBg;

  const inactiveColor = isDark
    ? ORG_PALETTE.segmentedTabs.inactiveColorDk
    : ORG_PALETTE.segmentedTabs.inactiveColor;

  const hoverBg = isDark
    ? ORG_PALETTE.segmentedTabs.hoverBgDk
    : ORG_PALETTE.segmentedTabs.hoverBg;

  const borderColor = isDark ? "#3f3f46" : "#e2e8f0";

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexWrap: wrap ? "wrap" : "nowrap",
        width: wrap ? "auto" : "100%",
        maxWidth: "100%",
        backgroundColor: containerBg,
        border: `1px solid ${borderColor}`,
        borderRadius: ORG_PALETTE.radius.tab || "12px",
        padding: "4px",
        minHeight: dense ? "40px" : "44px",
        alignItems: "center",
        rowGap: wrap ? "4px" : 0,
        boxShadow: isDark
          ? "inset 0 1px 2px rgba(0, 0, 0, 0.3)"
          : "inset 0 1px 2px rgba(0, 0, 0, 0.04)",
        ...sx,
      }}
    >
      {options.map((tab, index) => {
        const isActive = selected === tab.value;
        const isPrevActive = index > 0 && options[index - 1].value === selected;
        const IconComponent = tab.tabIcon || tab.icon;

        return (
          <React.Fragment key={tab.value}>
            {/* Subtle Divider between non-active adjacent tabs */}
            {!wrap && index > 0 && (
              <Box
                sx={{
                  width: "1px",
                  height: dense ? "18px" : "20px",
                  backgroundColor: (isActive || isPrevActive)
                    ? "transparent"
                    : (isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.1)"),
                  margin: "0 2px",
                  flexShrink: 0,
                  transition: "background-color 0.2s ease",
                }}
              />
            )}

            <Button
              disableRipple
              onClick={() => setSelected(tab.value)}
              sx={{
                flex: wrap ? "0 0 auto" : 1,
                borderRadius: ORG_PALETTE.radius.tabItem || "10px",
                padding: dense ? "5px 12px" : "7px 16px",
                textTransform: "none",
                fontFamily: tok.font.family,
                fontSize: dense ? "13px" : (tok.font.size.tabLabel || "14px"),
                fontWeight: isActive ? 600 : 500,
                lineHeight: 1.4,
                minWidth: 0,
                whiteSpace: "nowrap",
                margin: "1px",
                backgroundColor: isActive ? activeColor : "transparent",
                color: isActive ? "#ffffff" : inactiveColor,
                boxShadow: isActive
                  ? (ORG_PALETTE.segmentedTabs.activeShadow || "0 2px 8px rgba(101, 157, 51, 0.3)")
                  : "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: isActive
                    ? activeColor
                    : hoverBg,
                  color: isActive ? "#ffffff" : (isDark ? "#ffffff" : "#0f172a"),
                  transform: isActive ? "none" : "translateY(-0.5px)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
              }}
              startIcon={
                IconComponent ? (
                  React.isValidElement(IconComponent) ? (
                    IconComponent
                  ) : (
                    React.createElement(IconComponent, {
                      style: {
                        fontSize: dense ? 15 : 17,
                        color: isActive ? "#ffffff" : "inherit",
                        marginRight: 2,
                      },
                    })
                  )
                ) : undefined
              }
            >
              {t(tab.label)}
              {tab.badge !== undefined && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 0.75,
                    py: 0.1,
                    borderRadius: "9999px",
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor: isActive
                      ? "rgba(255, 255, 255, 0.25)"
                      : (isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"),
                    color: isActive ? "#ffffff" : "inherit",
                  }}
                >
                  {tab.badge}
                </Box>
              )}
            </Button>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default SegmentedTabs;
